import { useState, useEffect, useRef } from "react";
import { FlaskConical, Search, Loader2, Trash2, ExternalLink, RotateCcw, Wand2, AlertTriangle, RefreshCw, Sparkles, PlusCircle, HelpCircle, Star } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import AutoResizeTextarea from "@/components/AutoResizeTextarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { BACKEND_URL, uid, CAUTIONARY_TALES } from "./constants";

// --- Inlined atoms (visual-edits plugin can't handle shared JSX modules) ---
const DEFAULT_TPP_ROWS = [
  "Indication / patient population",
  "Primary efficacy endpoint",
  "Efficacy target (magnitude)",
  "Duration of response",
  "Safety profile",
  "Route of administration & dosing",
];

function TppTable({ data, setData, persistTab }) {
  const [busy, setBusy] = useState(false);
  const rows = data.tpp_table && data.tpp_table.length > 0
    ? data.tpp_table
    : DEFAULT_TPP_ROWS.map((a, i) => ({ id: `tpp_seed_${i}`, attribute: a, minimum: "", ideal: "" }));

  const setRows = (nextRows) => {
    const next = { ...data, tpp_table: nextRows };
    setData(next);
    persistTab(next);
  };
  const setRowsNoPersist = (nextRows) => setData({ ...data, tpp_table: nextRows });

  const updateCell = (id, patch) => setRowsNoPersist(rows.map((r) => r.id === id ? { ...r, ...patch } : r));
  const addRow = () => setRows([...rows, { id: `tpp_${Date.now()}`, attribute: "", minimum: "", ideal: "" }]);
  const removeRow = (id) => setRows(rows.filter((r) => r.id !== id));

  const aiFill = async () => {
    setBusy(true);
    try {
      const ctx = { patient_population: data.patient_population, why_this_first: data.why_this_first, current_alternative: data.current_alternative, current_stage: data.current_stage, next_milestone: data.next_milestone };
      const res = await fetch(`${BACKEND_URL}/api/strategy/clinical/suggest-tpp-table`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ rows: rows.map(({ attribute }) => ({ attribute })), context: ctx }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.rows?.length) {
          // Preserve any founder-entered values; only overwrite empty cells
          const filled = d.rows.map((incoming, i) => {
            const existing = rows[i] || {};
            return {
              id: existing.id || incoming.id,
              attribute: existing.attribute || incoming.attribute,
              minimum: existing.minimum && existing.minimum.trim() ? existing.minimum : incoming.minimum,
              ideal: existing.ideal && existing.ideal.trim() ? existing.ideal : incoming.ideal,
            };
          });
          setRows(filled);
          toast.success("TPP table filled");
        } else toast.info("No rows returned");
      } else toast.error("Failed to fill TPP");
    } catch (e) { toast.error("Failed to fill TPP"); }
    setBusy(false);
  };

  return (
    <div data-testid="cp-tpp-table">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-slate-500">Minimum acceptable = the floor to secure approval AND reimbursement. Ideal = best-in-class.</p>
        <Button size="sm" variant="ghost" onClick={aiFill} disabled={busy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid="cp-tpp-ai-fill">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}<span className="ml-1 text-xs">AI fill entire TPP</span>
        </Button>
      </div>
      <div className="border rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 gap-0 bg-slate-50 border-b text-[11px] font-medium text-slate-500 uppercase tracking-wide">
          <div className="col-span-4 px-3 py-2 border-r">Attribute</div>
          <div className="col-span-4 px-3 py-2 border-r">Minimum acceptable</div>
          <div className="col-span-3 px-3 py-2 border-r">Ideal</div>
          <div className="col-span-1 px-3 py-2"></div>
        </div>
        {rows.map((row) => (
          <div key={row.id} className="grid grid-cols-12 gap-0 border-b last:border-b-0 hover:bg-slate-50/40" data-testid={`cp-tpp-row-${row.id}`}>
            <div className="col-span-4 border-r p-1">
              <Textarea value={row.attribute} onChange={(e) => updateCell(row.id, { attribute: e.target.value })} onBlur={() => setRows(rows)} rows={2} className="text-xs border-0 focus-visible:ring-1 resize-none min-h-[3.5rem]" placeholder="Attribute" data-testid={`cp-tpp-attr-${row.id}`} />
            </div>
            <div className="col-span-4 border-r p-1">
              <Textarea value={row.minimum} onChange={(e) => updateCell(row.id, { minimum: e.target.value })} onBlur={() => setRows(rows)} rows={2} className="text-xs border-0 focus-visible:ring-1 resize-none min-h-[3.5rem]" placeholder="Minimum acceptable value…" data-testid={`cp-tpp-min-${row.id}`} />
            </div>
            <div className="col-span-3 border-r p-1">
              <Textarea value={row.ideal} onChange={(e) => updateCell(row.id, { ideal: e.target.value })} onBlur={() => setRows(rows)} rows={2} className="text-xs border-0 focus-visible:ring-1 resize-none min-h-[3.5rem]" placeholder="Ideal value…" data-testid={`cp-tpp-ideal-${row.id}`} />
            </div>
            <div className="col-span-1 flex items-start justify-center pt-2">
              <button onClick={() => removeRow(row.id)} className="text-slate-300 hover:text-red-500" data-testid={`cp-tpp-del-${row.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
      <Button size="sm" variant="ghost" onClick={addRow} className="mt-2 text-teal-600" data-testid="cp-tpp-add"><PlusCircle className="w-4 h-4 mr-1" /> Add attribute</Button>
    </div>
  );
}

function FieldWand({ tab, field, fieldLabel, current, context, onAnswer, testid }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/clinical/suggest-field`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ tab, field, fieldLabel, current, context }),
      });
      if (res.ok) { const d = await res.json(); if (d.answer) { onAnswer(d.answer); toast.success("AI draft ready"); } else toast.info("No draft returned"); }
      else toast.error("Failed to draft");
    } catch (e) { toast.error("Failed to draft"); }
    setBusy(false);
  };
  return (
    <Button size="sm" variant="ghost" onClick={run} disabled={busy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid={testid} title="AI draft an answer">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
      <span className="ml-1 text-xs">AI draft</span>
    </Button>
  );
}
function WandField({ tab, field, label, value, onChange, onBlur, rows = 3, placeholder, context, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-slate-800">{label}</Label>
        <FieldWand tab={tab} field={field} fieldLabel={label} current={value} context={context} onAnswer={(a) => onChange(a)} testid={`${testid}-wand`} />
      </div>
      <AutoResizeTextarea value={value || ""} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} minRows={rows} maxHeight={560} className="mt-1 text-sm leading-relaxed" placeholder={placeholder} data-testid={testid} />
    </div>
  );
}
function CautionaryTale({ tale, testid }) {
  return (
    <Card className="border-amber-200 bg-amber-50 sticky top-4" data-testid={testid}>
      <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-900 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Cautionary tale · {tale.title}</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-xs text-amber-900">
        <p>{tale.body}</p>
        <p className="font-semibold">Lesson: <span className="font-normal">{tale.lesson}</span></p>
        {tale.extra && <p className="pt-2 border-t border-amber-200 italic">{tale.extra}</p>}
      </CardContent>
    </Card>
  );
}
function KeyQuestionsSection({ tab, tabData, questions, onSetQuestions, persistTab, testidBase }) {
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState("");
  const suggest = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/clinical/suggest-key-questions`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ tab, tab_data: tabData, existing: questions.map((q) => q.text) }),
      });
      if (res.ok) {
        const d = await res.json();
        const next = [...questions, ...(d.questions || []).map((t) => ({ id: uid(), text: t }))];
        onSetQuestions(next);
        persistTab(next);
        if (d.questions?.length) toast.success(`Added ${d.questions.length} AI questions`);
        else toast.info("No suggestions returned");
      } else toast.error("Failed");
    } catch (e) { toast.error("Failed"); }
    setBusy(false);
  };
  const add = () => { if (!input.trim()) return; const next = [...questions, { id: uid(), text: input.trim() }]; onSetQuestions(next); persistTab(next); setInput(""); };
  const remove = (id) => { const next = questions.filter((q) => q.id !== id); onSetQuestions(next); persistTab(next); };
  return (
    <Card data-testid={`${testidBase}-section`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><HelpCircle className="w-4 h-4 text-teal-600" /> Key questions</CardTitle>
          <Button size="sm" variant="ghost" onClick={suggest} disabled={busy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid={`${testidBase}-suggest`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}<span className="ml-1 text-xs">AI suggest</span>
          </Button>
        </div>
        <p className="text-xs text-slate-500">Sharp open questions you should be able to answer to defend this section.</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {questions.length === 0 && <p className="text-xs text-slate-400 italic">No questions yet. Add your own or click AI suggest.</p>}
        {questions.map((q) => (
          <div key={q.id} className="flex items-start gap-2 border-l-2 border-l-teal-300 pl-3 py-1.5 group" data-testid={`${testidBase}-q-${q.id}`}>
            <span className="text-sm text-slate-700 flex-1 leading-snug">{q.text}</span>
            <button onClick={() => remove(q.id)} className="text-slate-300 hover:text-red-500 flex-shrink-0" data-testid={`${testidBase}-q-del-${q.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <div className="flex gap-2 pt-2 border-t">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add your own question…" className="text-sm h-9" data-testid={`${testidBase}-input`} />
          <Button size="sm" onClick={add} data-testid={`${testidBase}-add`}><PlusCircle className="w-4 h-4" /></Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ClinicalProofTab({ data, setData, persist, companyProfile }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);
  const set = (patch) => setData({ ...data, ...patch });
  const setField = (k) => (v) => set({ [k]: v });

  const persistTab = (nextClinicalProof) => persist({ clinical_proof: nextClinicalProof });

  const runSearch = async (searchQuery, replace = false) => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/clinical/search-trials`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ query: searchQuery, context: { patient_population: data.patient_population, current_alternative: data.current_alternative, minimum_profile: data.minimum_profile } }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.trials?.length) {
          const nextList = replace ? d.trials : [...(data.comparable_trials || []), ...d.trials];
          const nextCp = { ...data, comparable_trials: nextList };
          setData(nextCp); persistTab(nextCp);
          toast.success(d.auto_curated ? `Auto-curated ${d.trials.length} relevant trials` : `Added ${d.trials.length} trials`);
        } else toast.info(d.message || "No trials found");
      } else toast.error("Search failed");
    } catch (e) { toast.error("Search failed"); }
    setBusy(false);
  };

  // Auto-curate on first mount — always fire once we have companyProfile (or the
  // user's tab data). The backend will synthesise a search query from whatever
  // profile fields it has, so we don't gate on hasCtx here.
  const autoCurated = useRef(false);
  useEffect(() => {
    if (autoCurated.current) return;
    if (!companyProfile && !data.patient_population) return; // wait for profile load
    if ((data.comparable_trials || []).length > 0) return;
    autoCurated.current = true;
    setAutoLoading(true);
    runSearch("", true).finally(() => setAutoLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyProfile]);

  const removeTrial = (id) => {
    const nextCp = { ...data, comparable_trials: (data.comparable_trials || []).filter((t) => t.id !== id) };
    setData(nextCp); persistTab(nextCp);
  };
  const resetTab = () => {
    if (window.confirm("Reset the Clinical Proof tab? This clears all answers and trials.")) {
      const empty = { patient_population: "", why_this_first: "", current_alternative: "", minimum_profile: "", ideal_profile: "", tpp_table: [], current_stage: "", next_milestone: "", proposed_endpoint: "", success_criteria: "", key_questions: [], comparable_trials: [] };
      setData(empty); persistTab(empty); toast.success("Clinical Proof reset");
    }
  };
  const refresh = () => runSearch("", true);

  const ctx = { patient_population: data.patient_population, why_this_first: data.why_this_first, current_alternative: data.current_alternative, minimum_profile: data.minimum_profile, next_milestone: data.next_milestone };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="clinical-proof-tab">
      <div className="lg:col-span-2 space-y-4">
        {/* A. First Use Case */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><FlaskConical className="w-4 h-4 text-teal-600" /> A · First use case</CardTitle>
              <Button size="sm" variant="ghost" onClick={resetTab} className="h-7 px-2 text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid="cp-reset-btn"><RotateCcw className="w-3.5 h-3.5" /></Button>
            </div>
            <p className="text-xs text-slate-500">Which patient population are you targeting first — and why start there rather than a broader group?</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <WandField tab="clinical_proof" field="patient_population" label="First patient population" value={data.patient_population} onChange={setField("patient_population")} onBlur={persist} placeholder="e.g. Type 1 SMA infants under 6 months, AAV9-antibody-negative…" context={ctx} testid="cp-population" />
            <WandField tab="clinical_proof" field="why_this_first" label="Why this population first?" value={data.why_this_first} onChange={setField("why_this_first")} onBlur={persist} placeholder="Highest unmet need? Cleanest biological signal? Fastest path to proof? Regulatory precedent?" context={ctx} testid="cp-why-first" />
          </CardContent>
        </Card>

        {/* B. Current alternative */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><span className="text-teal-600 font-bold">B</span> · Current alternative</CardTitle>
            <p className="text-xs text-slate-500">What do these patients receive today? Your proof must beat this, not a hypothetical baseline.</p>
          </CardHeader>
          <CardContent>
            <WandField tab="clinical_proof" field="current_alternative" label="Current treatment or standard of care" value={data.current_alternative} onChange={setField("current_alternative")} onBlur={persist} placeholder="e.g. Onasemnogene abeparvovec (Zolgensma), nusinersen, best supportive care…" context={ctx} testid="cp-alternative" />
          </CardContent>
        </Card>

        {/* C. Minimum product profile */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><span className="text-teal-600 font-bold">C</span> · Product profile hypothesis</CardTitle>
            <p className="text-xs text-slate-500">The floor and the ceiling of what you must deliver, attribute by attribute.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <TppTable data={data} setData={setData} persistTab={persistTab} />
          </CardContent>
        </Card>

        {/* D. Next proof milestone */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><span className="text-teal-600 font-bold">D</span> · Next proof milestone</CardTitle>
            <p className="text-xs text-slate-500">The next study / readout that will prove the hypothesis — or kill it fast.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <WandField tab="clinical_proof" field="current_stage" label="Current stage" value={data.current_stage} onChange={setField("current_stage")} onBlur={persist} placeholder="e.g. IND-enabling tox studies underway" context={ctx} testid="cp-stage" />
            <WandField tab="clinical_proof" field="next_milestone" label="Next study / milestone" value={data.next_milestone} onChange={setField("next_milestone")} onBlur={persist} placeholder="e.g. Phase 1a dose escalation, N=12, single dose, 12-week follow-up" context={ctx} testid="cp-next" />
            <WandField tab="clinical_proof" field="proposed_endpoint" label="Proposed primary endpoint" value={data.proposed_endpoint} onChange={setField("proposed_endpoint")} onBlur={persist} placeholder="Ideally supports regulator + clinician + payer decisions." context={ctx} testid="cp-endpoint" />
            <WandField tab="clinical_proof" field="success_criteria" label="Success criteria (go / no-go)" value={data.success_criteria} onChange={setField("success_criteria")} onBlur={persist} placeholder="Numeric thresholds. What number would let you claim the hypothesis is proved?" context={ctx} testid="cp-success" />
          </CardContent>
        </Card>

        {/* Auto-curated comparable trials */}
        <Card data-testid="cp-trials-section">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><Sparkles className="w-4 h-4 text-teal-600" /> Comparable clinical trials <Badge variant="outline" className="text-[10px] ml-1">Auto-curated</Badge></CardTitle>
              <Button size="sm" variant="ghost" onClick={refresh} disabled={busy} className="h-7 px-2 text-teal-600 hover:bg-teal-50" data-testid="cp-refresh-btn" title="Refresh from ClinicalTrials.gov based on your latest inputs">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}<span className="ml-1 text-xs">Refresh suggestions</span>
              </Button>
            </div>
            <p className="text-xs text-slate-500">Pulled from ClinicalTrials.gov, ranked by relevance to your programme.</p>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-3">
              <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch(q, false)} placeholder="Or search a specific term (e.g. AAV DMD Phase 1)" className="text-sm" data-testid="cp-search-input" />
              <Button size="sm" onClick={() => runSearch(q, false)} disabled={busy} data-testid="cp-search-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}</Button>
            </div>
            <div className="space-y-2">
              {(data.comparable_trials || []).length === 0 && !busy && <p className="text-xs text-slate-400 italic">The AI will auto-curate trials from your company profile — or type a term above to run a specific search.</p>}
              {busy && (data.comparable_trials || []).length === 0 && <p className="text-xs text-teal-600 italic flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Auto-curating trials for your programme…</p>}
              {(data.comparable_trials || []).map((t) => (
                <div key={t.id} className="border rounded-lg p-3 bg-white" data-testid={`cp-trial-${t.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <a href={t.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-slate-800 hover:text-teal-700 flex-1 leading-snug">
                      {t.nct_id && <span className="text-[10px] text-slate-400 mr-1">{t.nct_id}</span>}{t.title} <ExternalLink className="w-3 h-3 inline text-slate-400" />
                    </a>
                    {typeof t.relevance_score === "number" && t.relevance_score > 0 && <Badge variant="secondary" className="text-[10px] flex items-center gap-1"><Star className="w-2.5 h-2.5" />{t.relevance_score}</Badge>}
                    <button onClick={() => removeTrial(t.id)} className="text-slate-300 hover:text-red-500" data-testid={`cp-trial-del-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  {t.why_relevant && <p className="text-[11px] text-teal-700 italic mb-2 leading-snug">Why relevant: {t.why_relevant}</p>}
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                    {[["Population", t.population], ["Comparator", t.comparator], ["Endpoint", t.endpoint], ["Route / dosing", t.route_dosing], ["Expected benefit", t.expected_benefit]].map(([k, v]) => v && (
                      <div key={k}><dt className="text-slate-400">{k}</dt><dd className="text-slate-700">{v}</dd></div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="lg:col-span-1"><CautionaryTale tale={CAUTIONARY_TALES.clinical_proof} testid="cp-cautionary" /></div>
    </div>
  );
}
